import React, { useEffect, useState } from 'react';
import { Modal, Switch, Spin, Empty } from 'antd';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getRequest, postRequest } from '../Utils/AppApi';
import {
    USER_PERMISSION_SCHEMA,
    USER_PERMISSION_GET,
    USER_PERMISSION_SAVE
} from '../Utils/appConstants';

const UserPermissionModal = ({ open, user, onClose, onSaved, theme }) => {
    const history = useHistory();
    const primaryColor = theme?.primary || '#C9262C';

    const [schema, setSchema] = useState([]);
    const [values, setValues] = useState({});
    const [remark, setRemark] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || schema.length > 0) return;
        getRequest(`${USER_PERMISSION_SCHEMA}`, history)
            .then((res) => {
                if (res.success === true) setSchema(res.data || []);
            })
            .catch(() => {});
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open || !user?.userId) return;

        setValues({});
        setRemark('');
        setLoading(true);

        getRequest(`${USER_PERMISSION_GET}?userId=${user.userId}`, history)
            .then((res) => {
                if (res.success === true && res.data) {
                    setValues(res.data.permissions || {});
                    setRemark(res.data.remark || '');
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [open, user?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleToggle = (key, checked) => {
        setValues((prev) => ({ ...prev, [key]: checked }));
    };

    const handleGrantAll = () => {
        const all = {};
        schema.forEach((p) => { all[p.key] = true; });
        setValues(all);
    };

    const handleRemoveAll = () => {
        const all = {};
        schema.forEach((p) => { all[p.key] = false; });
        setValues(all);
    };

    const handleSave = () => {
        if (!user?.userId) return;

        setSaving(true);
        const payload = {
            userId: user.userId,
            permissions: values,
            remark: remark || ''
        };

        postRequest(`${USER_PERMISSION_SAVE}`, payload, history)
            .then((res) => {
                if (res.success === true) {
                    toast.success(res.message || 'Permission saved successfully');
                    if (typeof onSaved === 'function') onSaved();
                    onClose();
                } else {
                    toast.error(res.message || 'Failed to save permission');
                }
            })
            .catch(() => toast.error('Failed to save permission'))
            .finally(() => setSaving(false));
    };

    const enabledCount = schema.filter((p) => values[p.key] === true).length;
    const totalCount = schema.length;

    return (
        <Modal
            title={
                <div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#1A1A2E' }}>
                        User Permissions
                    </div>
                    {user && (
                        <div style={{ fontSize: 16, fontWeight: 400, color: '#6B7280', marginTop: 4 }}>
                            {user.name}
                            {user.username ? ` · ${user.username}` : ''}
                        </div>
                    )}
                </div>
            }
            width={720}
            open={open}
            onOk={handleSave}
            onCancel={onClose}
            okText="Save Permissions"
            cancelText="Cancel"
            confirmLoading={saving}
            destroyOnClose
            styles={{
                body: { paddingTop: 16, paddingBottom: 12 }
            }}
            okButtonProps={{
                style: {
                    background: primaryColor,
                    borderColor: primaryColor,
                    fontWeight: 600,
                    fontSize: 16,
                    borderRadius: 8,
                    height: 44,
                    padding: '0 24px'
                }
            }}
            cancelButtonProps={{
                style: {
                    fontSize: 16,
                    borderRadius: 8,
                    height: 44,
                    padding: '0 24px'
                }
            }}
        >
            {loading ? (
                <div className="text-center py-5">
                    <Spin />
                </div>
            ) : schema.length === 0 ? (
                <Empty
                    description={
                        <span className="text-muted" style={{ fontSize: 16 }}>
                            Permission schema not loaded.<br />
                            Check <code>USER_PERMISSION_SCHEMA</code> endpoint.
                        </span>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            ) : (
                <>
                    {/* Header strip: count + bulk actions */}
                    <div
                        className="d-flex align-items-center justify-content-between mb-3"
                        style={{
                            padding: '14px 18px',
                            background: '#F9FAFB',
                            border: '1px solid #E5E7EB',
                            borderRadius: 10
                        }}
                    >
                        <div style={{ fontSize: 16, color: '#6B7280' }}>
                            <span style={{ fontWeight: 700, color: '#1A1A2E', fontSize: 18 }}>
                                {enabledCount}
                            </span>
                            {' '}of{' '}
                            <span style={{ fontWeight: 700, color: '#1A1A2E', fontSize: 18 }}>
                                {totalCount}
                            </span>
                            {' '}permissions enabled
                        </div>
                        <div className="d-flex">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-success mr-2"
                                style={{ borderRadius: 8, fontSize: 15, padding: '6px 16px' }}
                                onClick={handleGrantAll}
                            >
                                Grant All
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                style={{ borderRadius: 8, fontSize: 15, padding: '6px 16px' }}
                                onClick={handleRemoveAll}
                            >
                                Remove All
                            </button>
                        </div>
                    </div>

                    {/* Permission rows */}
                    <div className="d-flex flex-column" style={{ gap: 10 }}>
                        {schema.map((perm) => {
                            const isOn = values[perm.key] === true;
                            return (
                                <div
                                    key={perm.key}
                                    onClick={() => handleToggle(perm.key, !isOn)}
                                    className="d-flex align-items-center justify-content-between"
                                    style={{
                                        padding: '16px 20px',
                                        border: `1px solid ${isOn ? '#BBF7D0' : '#E5E7EB'}`,
                                        background: isOn ? '#F0FDF4' : '#FFFFFF',
                                        borderRadius: 10,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    <div style={{ lineHeight: 1.4 }}>
                                        <div
                                            style={{
                                                fontSize: 17,
                                                fontWeight: 600,
                                                color: '#1A1A2E'
                                            }}
                                        >
                                            {perm.label}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 13,
                                                color: '#9CA3AF',
                                                marginTop: 3,
                                                fontFamily: 'monospace'
                                            }}
                                        >
                                            {perm.key}
                                        </div>
                                    </div>

                                    <div
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ marginLeft: 20 }}
                                    >
                                        <Switch
                                            checked={isOn}
                                            onChange={(checked) => handleToggle(perm.key, checked)}
                                            style={{
                                                backgroundColor: isOn ? '#02A650' : undefined
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Remark */}
                    <div className="mt-3">
                        <label
                            style={{
                                fontSize: 15,
                                fontWeight: 600,
                                color: '#374151',
                                marginBottom: 8,
                                display: 'block'
                            }}
                        >
                            Remark <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span>
                        </label>
                        <textarea
                            className="form-control"
                            rows={3}
                            placeholder="Add a note for the audit log…"
                            value={remark}
                            onChange={(e) => setRemark(e.target.value)}
                            style={{
                                borderRadius: 8,
                                fontSize: 15,
                                resize: 'none',
                                borderColor: '#E5E7EB',
                                padding: '10px 14px'
                            }}
                        />
                    </div>
                </>
            )}
        </Modal>
    );
};

export default UserPermissionModal;